import types from 'actions/types'
import {combineReducers} from 'redux'
import {get, isEmpty, isNil} from 'lodash'
import Immutable from 'seamless-immutable'
import search from './search'
import selected from './selected'
import grid from './grid'
import contactLists from './contactLists'
import listBuilder from './listBuilder'
import bulkSubscribe from './bulkSubscribe'
import contactListView from './contactListView'
import {FIELD_SELECT_MODE} from 'helpers/contacts'

export const openCompanies = (state = [], action) => {
  switch (action.type) {
    case types.CONTACTS_RECENTS_ADD: {
      const {contact, company} = action

      const newCompany = {
        id: company.id,
        name: company.name,
        type: company.type,
        companyLogoImage: company.companyLogoImage,
        contact: isNil(contact)
            ? null
            : {
              id: contact.id,
              forename: contact.forename,
              surname: contact.surname,
              email: contact.email,
            },
      }

      // Remove any existing entry and add the new company at the start of the list
      return [
        newCompany,
        ...state.filter(curCompany => curCompany.id !== company.id),
      ]
    }

    case types.CONTACTS_RECENTS_REMOVE:
      return state.filter(r => r.id !== action.companyId)  // remove the company

    case types.CONTACTS_RECENTS_CLEAR:
      return [] // clear the list

    case types.CONTACTS_RECENTS_SET:
      return action.companies // overwrite the list

    case types.QUOTING_VIEW_QUOTES_UPDATE_FILTERS:
    case types.INVOICING_VIEW_INVOICES_UPDATE_FILTERS:
      const {company} = action.filters
      const ret = [...state]
      if (company) {
        // Ensure selected company is open
        if (!ret.some(r => r.id === company.id)) {
          const {id, name, type} = company
          ret.push({id, name, type})
        }
      }
      return ret
    case types.CONTACTS_COMPANY_EDIT_SUCCESS: {
      return state.map(openCompany => {
        return openCompany.id === action.data.id
            ? {
              ...openCompany,
              name: action.data.name,
            }
            : openCompany
      })
    }
    case types.CONTACTS_CONTACT_EDIT_SUCCESS: {
      return state.map(openCompany => {
        if (openCompany.contact.id === action.data.id) {
          const contact = get(openCompany, 'contact', null)
          const updatedContact = action.data

          return isNil(contact)
              ? openCompany
              : {
                ...openCompany,
                contact: {
                  ...contact,
                  forename: updatedContact.forename,
                  surname: updatedContact.surname,
                  email: updatedContact.email,
                },
              }
        }

        return openCompany
      })
    }
    case types.CONTACTS_COMPANY_DELETE_SUCCESS:
      return state.filter(openCompany => !openCompany.id !== action.companyId)

    case types.CONTACTS_UPLOAD_COMPANY_IMAGE_SUCCESS:
      return state.map(openCompany => {
        const companyId = get(openCompany, 'id', null)
        const companyImageCompanyId = get(action, 'data.companyId', null)
        return companyId === companyImageCompanyId
            ? {
              ...openCompany,
              companyLogoImage: action.data,
            }
            : openCompany
      })

    case types.CONTACTS_REMOVE_COMPANY_IMAGE_SUCCESS:
      return state.map(openCompany => {
        const companyId = get(openCompany, 'company.id', null)
        const companyImageCompanyId = get(action, 'companyId', null)
        return companyId === companyImageCompanyId
            ? {
              ...openCompany,
              company: {
                ...openCompany.company,
                companyLogoImage: null,
              },
            }
            : openCompany
      })

    default:
      return state
  }
}

const recentContacts = (state = [], action) => {
  switch (action.type) {
    case types.CONTACTS_RECENTS_ADD: {
      if (isEmpty(action.contact)) return state // not opening a contact

      // Add the contact being opened at the beginning of the list
      const {id, companyId} = action.contact
      return [
        {id, companyId},
        ...state.filter(v => v.id !== id),  // remove if already in the list
      ]
    }

    case types.CONTACTS_RECENTS_REMOVE:
      if (action.contactId) return state.filter(r => r.id !== action.contactId)  // remove the contact
      return state.filter(r => r.companyId !== action.companyId)  // remove the whole company

    case types.CONTACTS_RECENTS_CLEAR:
      return [] // clear the list

    case types.CONTACTS_RECENTS_SET:
      // Remove contacts for companies no longer in the list
      return state.filter(r => action.companies.some(c => c.id === r.companyId))

    default:
      return state
  }
}

// deprecated
const contactFields = (state = {}, action) => {
  switch (action.type) {
    case types.CONTACTS_TOGGLE_CONTACT_FIELD:
      return {
        ...state,
        [action.key]: action.visible,
      }
    default:
      return state
  }
}

// deprecated
const companyFields = (state = {}, action) => {
  switch (action.type) {
    case types.CONTACTS_TOGGLE_COMPANY_FIELD:
      return {
        ...state,
        [action.key]: action.visible,
      }
    default:
      return state
  }
}

// deprecated
const individualFields = (state = {}, action) => {
  switch (action.type) {
    case types.CONTACTS_TOGGLE_INDIVIDUAL_FIELD:
      return {
        ...state,
        [action.key]: action.visible,
      }
    default:
      return state
  }
}

const selectedContactFields = (state = {}, action) => {
  switch (action.type) {
    case types.CONTACTS_TOGGLE_CONTACT_FIELD:
      return {
        ...state,
        [action.key]: action.visible,
      }
    default:
      return state
  }
}

const selectedCompanyFields = (state = {}, action) => {
  switch (action.type) {
    case types.CONTACTS_TOGGLE_COMPANY_FIELD:
      return {
        ...state,
        [action.key]: action.visible,
      }
    default:
      return state
  }
}

const selectedIndividualFields = (state = {}, action) => {
  switch (action.type) {
    case types.CONTACTS_TOGGLE_INDIVIDUAL_FIELD:
      return {
        ...state,
        [action.key]: action.visible,
      }
    default:
      return state
  }
}

export const fieldSelectorOpen = (state = false, action) => {
  switch (action.type) {
    case types.CONTACTS_TOGGLE_FIELD_SELECTOR:
      return action.toggle
    default:
      return state
  }
}

export const fieldSelectMode = (state = FIELD_SELECT_MODE.CONTACT, action) => {
  switch (action.type) {
    case types.CONTACTS_SET_FIELD_SELECT_MODE:
      return action.mode
    default:
      return state
  }
}

const companyImageDates = (state = Immutable({default: new Date().getTime()}), action) => {
  switch (action.type) {
    case types.CONTACTS_UPLOAD_COMPANY_IMAGE_SUCCESS: {
      return {
        ...state,
        [action.data.companyId]: new Date().getTime(),
      }
    }
    case types.CONTACTS_REMOVE_COMPANY_IMAGE_SUCCESS: {
      return {
        ...state,
        [action.companyId]: new Date().getTime(),
      }
    }
    default:
      return state
  }
}

export const recentQuotesReducer = mapKey => (state = Immutable({}), action) => {
  switch (action.type) {
    case types.QUOTING_LOAD_QUOTE_SUCCESS:
      const newState = {...state}
      const orderId = action.data.id
      const mapId = action.data[mapKey]

      Object.keys(newState).forEach(id => {
        if (newState[id] === orderId) newState[id] = null
      })

      newState[mapId] = orderId
      return newState

    default:
      return state
  }
}

export const recentPurchaseOrdersReducer = mapKey => (state = Immutable({}), action) => {
  switch (action.type) {
    case types.PURCHASING_LOAD_PURCHASE_ORDER_SUCCESS:
      const newState = {...state}
      const purchaseOrderId = action.data.id
      const mapId = action.data[mapKey]

      Object.keys(newState).forEach(id => {
        if (newState[id] === purchaseOrderId) newState[id] = null
      })

      newState[mapId] = purchaseOrderId
      return newState

    default:
      return state
  }
}

export const isBulkSaving = (state = false, action) => {
  switch (action.type) {
    case types.CONTACTS_BULK_SAVE_BEGIN:
      return true
    case types.CONTACTS_BULK_SAVE_FINISH:
      return false
    default:
      return state
  }
}

export const bulkSaveProgress = (state = 0, action) => {
  switch (action.type) {
    case types.CONTACTS_BULK_SAVE_BEGIN:
      return 0
    case types.CONTACTS_BULK_SAVE_FINISH:
      return 0
    case types.CONTACTS_BULK_SAVE_UPDATE:
      return action.total === 0
          ? 0
          : action.current / action.total * 100
    default:
      return state
  }
}

export const isDeletingCompany = (state = false, action) => {
  switch (action.type) {
    case types.CONTACTS_COMPANY_DELETE_ATTEMPT:
      return true
    case types.CONTACTS_COMPANY_DELETE_SUCCESS:
      return false
    case types.CONTACTS_COMPANY_DELETE_FAILURE:
      return false
    default:
      return state
  }
}

export const isDeletingContact = (state = false, action) => {
  switch (action.type) {
    case types.CONTACTS_CONTACT_DELETE_ATTEMPT:
      return true
    case types.CONTACTS_CONTACT_DELETE_SUCCESS:
      return false
    case types.CONTACTS_CONTACT_DELETE_FAILURE:
      return false
    default:
      return state
  }
}

export default combineReducers({
  search,
  openCompanies,
  recentContacts,
  contactFields,
  companyFields,
  individualFields,
  selectedContactFields,
  selectedCompanyFields,
  selectedIndividualFields,
  fieldSelectorOpen,
  fieldSelectMode,
  companyImageDates,
  selected,
  grid,
  contactLists,
  contactListView,
  listBuilder,
  bulkSubscribe,
  isBulkSaving,
  bulkSaveProgress,
  isDeletingCompany,
  isDeletingContact,
  recentCompanyQuotes: recentQuotesReducer('companyId'),
  recentContactQuotes: recentQuotesReducer('contactId'),
  recentCompanyPurchaseOrders: recentPurchaseOrdersReducer('companyId'),
  recentContactPurchaseOrders: recentPurchaseOrdersReducer('contactId'),
})
